import { areHostile } from '../../../../shared/creatureDisposition';
import { hasSubclassFeature } from '../../../../shared/rules/subclasses/index.mjs';
import { isMarkedByAttacker } from '../../../../shared/rules/ranger.mjs';
import { SUPERIOR_HUNTERS_PREY_FEATURE_ID, SUPERIOR_HUNTERS_PREY_RANGE_FEET } from '../../../../shared/rules/subclasses/ranger/huntersPrey.mjs';
import { minimumFootprintDistanceFeet } from '../../utils/pushGeometryCore.mjs';
import apiFetch from '../../../../utils/apiFetch';

/**
 * Superior Hunter's Prey (Hunter 11): creatures the mark's extra damage can also reach after this hit, hostile ones within
 * 30 feet of the marked creature, once per turn. Empty when it doesn't apply.
 */
export const superiorHuntersPreyTargets = ({ form, characterId, markedId, result, combatState, spatialContext }) => {
  const selfId = String(characterId), marked = String(markedId);
  if (!['hit', 'critical-hit'].includes(result?.outcome) || !(Number(result?.damageApplied) > 0)) return [];
  if (!hasSubclassFeature(form, SUPERIOR_HUNTERS_PREY_FEATURE_ID) || !Number.isInteger(combatState?.turnSequence)) return [];
  if (!isMarkedByAttacker(combatState?.activeEffects, selfId, marked)) return [];
  if (combatState?.turnFeatureUsage?.[selfId]?.[SUPERIOR_HUNTERS_PREY_FEATURE_ID]) return [];
  const tokens = spatialContext?.tokens || {}, entities = spatialContext?.entities || {};
  if (!tokens[marked]) return [];
  return Object.keys(tokens).filter(id => id !== selfId && id !== marked && !(Number(entities[id]?.currentHp) <= 0)).map(id => {
    const entity = entities[id] || {};
    const distance = minimumFootprintDistanceFeet({ sourceToken: tokens[marked], targetToken: tokens[id], sourceSize: entities[marked]?.size, targetSize: entity.size, map: spatialContext?.map });
    return { id, name: entity.label || entity.name || id, distance, hostile: areHostile({ ...form, enemy: false }, entity) };
  }).filter(entry => entry.hostile && entry.distance !== null && entry.distance <= SUPERIOR_HUNTERS_PREY_RANGE_FEET);
};

export const createSuperiorHuntersPreyDecision = ({ resolutionId, sides, targets }) => ({
  id: `${resolutionId}:${SUPERIOR_HUNTERS_PREY_FEATURE_ID}`, featureId: SUPERIOR_HUNTERS_PREY_FEATURE_ID, title: 'Superior Hunter’s Prey', label: 'Superior Hunter’s Prey',
  prompt: `Also deal your Hunter’s Mark damage (1d${sides} Force) to a creature within ${SUPERIOR_HUNTERS_PREY_RANGE_FEET} feet of the marked creature?`,
  choices: [...targets.map(target => ({ id: target.id, action: 'apply', label: `${target.name} · ${target.distance} ft` })), { id: 'skip', label: 'No' }],
});

export const applySuperiorHuntersPrey = async (campaignId, body) => {
  const response = await apiFetch(`/campaigns/${encodeURIComponent(campaignId)}/ranger/superior-hunters-prey`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || 'Superior Hunter’s Prey could not be applied.');
  return result;
};
